import * as React from "react"
import { HostingCertificate } from "@modules/graphql/types"
import {
  Heading,
  ThemeCss,
  Button,
  Notification,
  Text,
  Spacer,
} from "gatsby-interface"
import { format } from "date-fns"
import { gatsbyCloudCdnIntegrationConfig as text } from "@modules/locales/default.js"
import { MdError, MdLockOutline, MdLock } from "react-icons/md"
import { lockIcon } from "../styles/gatsbyCloudCdn"
import { stringifyCertDomains } from "../../shared/utils/stringifyCertDomains"
import { formatCertSerial } from "../../shared/utils/formatCertSerial"
import { useFlags } from "@modules/featureFlags"

const rowCss: ThemeCss = _theme => ({
  alignItems: `center`,
  display: `flex`,
  justifyContent: `space-between`,
})

const headingCss: ThemeCss = theme => ({
  fontFamily: theme.fonts.body,
  fontWeight: theme.fontWeights.semiBold,
  paddingLeft: theme.space[8],
  fontSize: theme.fontSizes[2],
  position: `relative`,
})

const refinedHeadingCss: ThemeCss = theme => [
  headingCss(theme),
  {
    fontSize: theme.fontSizes[1],
    display: `flex`,
    alignItems: `center`,
    margin: 0,

    svg: {
      color: theme.colors.green[60],
      position: `absolute`,
      transform: `translate(-150%, -5%)`,
      width: theme.space[6],
      height: theme.space[6],
    },
  },
]

const managedCss: ThemeCss = theme => ({
  paddingLeft: theme.space[8],
  marginTop: theme.space[4],
})

const detailsCss: ThemeCss = theme => ({
  display: `grid`,
  fontSize: theme.fontSizes[1],
  gridTemplateColumns: `max-content 1fr`,
  gridColumnGap: theme.space[6],
  gridRowGap: theme.space[2],
  paddingLeft: theme.space[8],
  color: theme.colors.grey[90],
  marginTop: theme.space[4],

  dt: {
    fontWeight: theme.fontWeights.body,
  },
  dd: {
    margin: 0,
    fontWeight: theme.fontWeights.semiBold,
  },
})

const refainedDetailsCss: ThemeCss = theme => ({
  color: theme.colors.grey[60],
  gridColumnGap: theme.space[9],

  dd: {
    color: theme.colors.grey[80],
    fontWeight: theme.fontWeights.body,
  },
})

const notificationTitleCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[1],
  fontFamily: theme.fonts.body,
  fontWeight: theme.fontWeights.body,
  color: theme.colors.grey[90],
  marginTop: 0,
  marginBottom: theme.space[2],
})

const notificationMessageCss: ThemeCss = theme => ({
  marginBottom: theme.space[3],
  marginRight: theme.space[3],
  fontSize: theme.fontSizes[0],
})

const notificationCss: ThemeCss = theme => ({
  marginTop: `${theme.space[3]}`,
})

const errorIconCss: ThemeCss = theme => ({
  "&&": {
    color: theme.colors.red[80],
  },
})

function ErrorIcon(props: React.ComponentPropsWithoutRef<"svg">) {
  return <MdError {...props} css={errorIconCss} />
}

export type SslCertificateDetailsProps = {
  certificate: HostingCertificate
  onRemove?: () => void
  allowToEdit?: boolean
}
export function SslCertificateDetails({
  certificate,
  onRemove,
  allowToEdit,
  ...rest
}: SslCertificateDetailsProps) {
  const {
    issuer,
    domains,
    createdAt,
    notAfter,
    managed,
    canDelete,
    serialNumber,
    errors,
  } = certificate
  const { flags } = useFlags()
  const certDomains = stringifyCertDomains(domains)

  return (
    <React.Fragment>
      <div css={rowCss}>
        <Heading css={refinedHeadingCss} as="h5">
          <MdLock /> {issuer}
          <Spacer size={4} direction="horizontal" />
          <Text size="S">
            {managed ? text.labels.managed : text.labels.custom}
          </Text>
        </Heading>

        {allowToEdit && canDelete && (
          <Button onClick={onRemove} variant="GHOST" size="S">
            {text.actions.remove}
          </Button>
        )}
      </div>
      {!!errors && errors.length > 0 && (
        <Notification
          css={notificationCss}
          variant="SECONDARY"
          tone="DANGER"
          content={
            <div>
              <Heading css={notificationTitleCss} as="h5">
                Invalid certificate
              </Heading>
              {errors.map(error => {
                return (
                  <Text key={error} css={notificationMessageCss}>
                    {error}
                  </Text>
                )
              })}
            </div>
          }
          Icon={ErrorIcon}
        />
      )}

      <dl
        css={theme => [detailsCss(theme), refainedDetailsCss(theme)]}
        {...rest}
      >
        <dt>{text.labels.domains}</dt>
        <dd>{certDomains}</dd>
        <dt>{text.labels.created}</dt>
        <dd>{format(new Date(createdAt), `MMM d, Y `)}</dd>
        <dt>{text.labels.expires}</dt>
        <dd>{format(new Date(notAfter), `MMM d, Y`)}</dd>
        <dt>{text.labels.serialNumber}</dt>
        <dd>{formatCertSerial(serialNumber)}</dd>
      </dl>
    </React.Fragment>
  )
}

const detailCss: ThemeCss = theme => ({
  fontWeight: theme.fontWeights.body,
  fontSize: theme.fontSizes[0],
  color: theme.colors.grey[60],
})

export type SslCertificateIsGeneratingProps = {
  domain: string
}
export function SslCertificateIsGenerating({
  domain,
}: SslCertificateIsGeneratingProps) {
  return (
    <Heading css={headingCss} as="h5">
      <MdLockOutline css={lockIcon} /> {text.messages.generatingSslCertificate}{" "}
      <span css={detailCss}>{`(${domain})`}</span>
    </Heading>
  )
}
